import type { CSSProperties, ReactNode } from "react";

/**
 * ほろりばの広場の飾り（参照元：Holoriba-homepage app/(home)/page.tsx の hero-sky と、src/components/RibatanMessage.tsx）。
 * どれも飾りなので、読み上げには出さない（りばたんのひとことだけは文として読む）。
 */

/* ---------------------------------------------------------------- 舞う紙片（ロゴの3色の三角） */

type BitColor = "cyan" | "petal" | "lavender";

/** 位置・大きさ・速さは決め打ちの疑似乱数（参照元と同じ式。描くたびに散らばり方が変わらないように） */
function makeBits(count: number, seed: number) {
  return Array.from({ length: count }, (_, i) => {
    const r = (n: number) => {
      const v = Math.sin((i + 1 + seed * 31) * 12.9898 + n * 78.233) * 43758.5453;
      return v - Math.floor(v);
    };
    return {
      x: Math.round(6 + r(1) * 86),
      y: Math.round(10 + r(2) * 70),
      s: +(0.55 + r(3) * 0.8).toFixed(2),
      d: +(r(4) * 1.2).toFixed(2),
      w: +(8 + r(5) * 7).toFixed(1),
      r: Math.round(r(6) * 360),
      c: (["cyan", "petal", "lavender"] as BitColor[])[i % 3],
    };
  });
}

export function SkyBits({ count = 7, seed = 0 }: { count?: number; seed?: number }) {
  return (
    <span className="sky-bits" aria-hidden="true" data-loop>
      {makeBits(count, seed).map((b, i) => (
        <span
          key={i}
          className={`sky-bit sky-bit--${b.c}`}
          style={
            { "--x": `${b.x}%`, "--y": `${b.y}%`, "--s": b.s, "--d": `${b.d}s`, "--w": `${b.w}s`, "--r": `${b.r}deg` } as CSSProperties
          }
        >
          <span className="sky-bit__chip" />
        </span>
      ))}
    </span>
  );
}

/* ---------------------------------------------------------------- りばたん */

export type RibatanPose = "hello" | "wait" | "lost";

/** りばたんの絵（参照元 public/ribatan。飾りなので alt は空） */
export function Ribatan({
  pose,
  className = "",
  sizes = "(max-width: 600px) 40vw, 200px",
}: {
  pose: RibatanPose;
  className?: string;
  sizes?: string;
}) {
  return (
    <img
      className={`ribatan ribatan--${pose} ${className}`.trim()}
      src={`/ribatan/ribatan-${pose}-320.webp`}
      srcSet={`/ribatan/ribatan-${pose}-320.webp 320w, /ribatan/ribatan-${pose}-640.webp 640w`}
      sizes={sizes}
      width={320}
      height={266}
      alt=""
      decoding="async"
    />
  );
}

/**
 * りばたんが出てくる案内（空っぽ・迷子・読み込みの失敗）。
 * 何が起きたか、どうすればよいかは、りばたんのひとことではなく、見出し・本文・ボタンで伝える（参照元と同じ決まり）
 */
export function RibatanNote({
  pose,
  say,
  title,
  titleAs = "p",
  children,
  size = "m",
}: {
  pose: RibatanPose;
  /** りばたんのひとこと */
  say: string;
  title: string;
  titleAs?: "p" | "h2";
  children?: ReactNode;
  size?: "s" | "m";
}) {
  const Title = titleAs;
  return (
    <div className={`ribatan-note ribatan-note--${size}`}>
      <Ribatan pose={pose} className="ribatan-note__art" sizes={size === "s" ? "120px" : "(max-width: 600px) 44vw, 200px"} />
      <p className="ribatan-note__say">
        <span className="visually-hidden">りばたん：</span>
        {say}
      </p>
      <Title className="ribatan-note__title">{title}</Title>
      {children && <div className="ribatan-note__body">{children}</div>}
    </div>
  );
}
